import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import styles from "./FollowListPage.module.css";
import Header from "./Header";

import {
  useGetFollowingQuery,
  useUnfollowUserMutation,
  useFollowUserMutation,
} from "../store/api/followApi";

import { useAuth } from "../hooks/useAuth";

export default function FollowingPage() {
  const { userId: userIdParam } = useParams();
  const navigate = useNavigate();

  const { userId } = useAuth();

  const [unfollowUser] = useUnfollowUserMutation();
  const [followUser] = useFollowUserMutation();

  const [loadingId, setLoadingId] = useState(null);
  const [loadingFollowId, setLoadingFollowId] = useState(null);

  // if userId is in URL → visiting someone else's profile
  // otherwise own profile
  const targetUserId = userIdParam ?? userId;
  const isOwnProfile = targetUserId === userId;

  const {
    data: following,
    isLoading,
    isError,
  } = useGetFollowingQuery(targetUserId);

  // unfollow when viewing own profile
  const handleUnfollow = async (e, id) => {
    e.stopPropagation();
    setLoadingId(id);

    try {
      await unfollowUser(id).unwrap();
    } catch (err) {
      console.error("Unfollow failed", err);
    } finally {
      setLoadingId(null);
    }
  };

  // follow/unfollow toggle when viewing another user's following list
  const handleFollowToggle = async (e, targetId, isFollowing) => {
    e.stopPropagation();
    setLoadingFollowId(targetId);

    try {
      if (isFollowing) {
        await unfollowUser(targetId).unwrap();
      } else {
        await followUser(targetId).unwrap();
      }
    } catch (err) {
      console.error("Follow toggle failed", err);
    } finally {
      setLoadingFollowId(null);
    }
  };

  if (isLoading) {
    return (
      <>
        <Header />

        <div className={styles.container}>
          <p>Loading following...</p>
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Header />

        <div className={styles.container}>
          <p>Failed to load following</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />

      <div className={styles.container}>
        <div className={styles.header}>
          <button onClick={() => navigate(-1)} className={styles.backBtn}>
            ← Back
          </button>

          <h1>Following ({following?.length || 0})</h1>

          <div className={styles.headerSpacer} />
        </div>

        <div className={styles.listContainer}>
          {following && following.length > 0 ? (
            following.map((user) => (
              <div
                key={user.id}
                className={styles.userCard}
                onClick={() => navigate(`/profile/${user.id}`)}
              >
                {user.profilePhotoUrl ? (
                  <img
                    src={user.profilePhotoUrl}
                    alt={user.name}
                    className={styles.avatar}
                  />
                ) : (
                  <div className={styles.noPhoto}>
                    {user.name
                      ?.split(" ")
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase() || "?"}
                  </div>
                )}

                <div className={styles.userInfo}>
                  <h3>{user.name}</h3>

                  <p className={styles.userType}>{user.userType}</p>

                  {user.bio && <p className={styles.bio}>{user.bio}</p>}
                </div>

                {/* OWN PROFILE → unfollow button */}
                {isOwnProfile && (
                  <button
                    className={styles.unfollowBtn}
                    disabled={loadingId === user.id}
                    onClick={(e) => handleUnfollow(e, user.id)}
                  >
                    {loadingId === user.id ? "..." : "Unfollow"}
                  </button>
                )}

                {/* OTHER USER PROFILE → follow/unfollow toggle */}
                {!isOwnProfile && user.id !== userId && (
                  <button
                    className={
                      user.isFollowing ? styles.unfollowBtn : styles.followBtn
                    }
                    disabled={loadingFollowId === user.id}
                    onClick={(e) =>
                      handleFollowToggle(e, user.id, user.isFollowing)
                    }
                  >
                    {loadingFollowId === user.id
                      ? "..."
                      : user.isFollowing
                        ? "Unfollow"
                        : "Follow"}
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>
              <p>Not following anyone yet</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
